import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { getProductById } from '../../data/products';

const ProductImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'cover',
  borderRadius: '4px',
});

const ProductTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  marginBottom: '1rem',
});

const Price = styled(Typography)({
  fontSize: '1.5rem',
  color: '#1a1a1a',
  marginTop: '1rem',
});

const AddToCartButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem',
  marginTop: '2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
  },
});

const OptionBox = styled(Paper)({
  padding: '1.5rem',
  marginBottom: '1rem',
  border: '1px solid #eee',
  borderRadius: '4px',
});

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const product = getProductById(id);

  const [selectedMetal, setSelectedMetal] = useState('');
  const [selectedDiamond, setSelectedDiamond] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!product) {
    return (
      <Container>
        <Box sx={{ py: 8 }}>
          <Alert severity="error">Product not found</Alert>
          <Button
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    );
  }

  const handleAddToCart = () => {
    if (!selectedMetal) return;

    const variant = product.variants.find(v => v.metal === selectedMetal);
    let totalPrice = variant.price;
    let description = `${product.name} - ${selectedMetal}`;

    if (selectedDiamond && product.diamonds) {
      const diamond = product.diamonds.options.find(d => d.type === selectedDiamond);
      totalPrice += diamond.price;
      if (product.diamonds.settingCost) {
        totalPrice += product.diamonds.settingCost;
      }
      description += ` with ${selectedDiamond} diamonds`;
    }

    addToCart({
      id: `${product.id}-${selectedMetal}-${selectedDiamond || 'no-diamond'}`,
      name: description,
      price: totalPrice,
      image: product.image,
      category: product.category,
      quantity: 1,
    });

    setSnackbarOpen(true);
  };

  return (
    <Container>
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <ProductImage src={product.image} alt={product.name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProductTitle variant="h1">{product.name}</ProductTitle>
              <Typography variant="body1" color="textSecondary" paragraph>
                {product.description}
              </Typography>

              <OptionBox elevation={0}>
                <Typography variant="h6" gutterBottom>
                  Select Metal
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Metal Type</InputLabel>
                  <Select
                    value={selectedMetal}
                    onChange={(e) => setSelectedMetal(e.target.value)}
                    label="Metal Type"
                  >
                    {product.variants.map((variant) => (
                      <MenuItem key={variant.metal} value={variant.metal}>
                        {variant.metal} - ${variant.price}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </OptionBox>

              {product.diamonds && (
                <OptionBox elevation={0}>
                  <Typography variant="h6" gutterBottom>
                    Add Diamonds
                  </Typography>
                  {product.diamonds.carats && (
                    <Typography color="textSecondary" gutterBottom>
                      {product.diamonds.carats} carats
                    </Typography>
                  )}
                  <FormControl fullWidth>
                    <InputLabel>Diamond Type</InputLabel>
                    <Select
                      value={selectedDiamond}
                      onChange={(e) => setSelectedDiamond(e.target.value)}
                      label="Diamond Type"
                    >
                      <MenuItem value="">No diamonds</MenuItem>
                      {product.diamonds.options.map((option) => (
                        <MenuItem key={option.type} value={option.type}>
                          {option.type} - ${option.price}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {product.diamonds.settingCost && (
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      Stone setting cost: ${product.diamonds.settingCost}
                    </Typography>
                  )}
                </OptionBox>
              )}

              <Price variant="h2">
                Total: $
                {(() => {
                  let total = 0;
                  if (selectedMetal) {
                    total += product.variants.find(v => v.metal === selectedMetal).price;
                  }
                  if (selectedDiamond && product.diamonds) {
                    total += product.diamonds.options.find(d => d.type === selectedDiamond).price;
                    if (product.diamonds.settingCost) {
                      total += product.diamonds.settingCost;
                    }
                  }
                  return total;
                })()}
              </Price>

              <AddToCartButton
                fullWidth
                onClick={handleAddToCart}
                disabled={!selectedMetal}
              >
                Add to Cart
              </AddToCartButton>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Added to cart"
      />
    </Container>
  );
};

export default ProductDetails;
